import { Stack, Typography } from '@mui/joy';
import { upperFirst } from 'lodash';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IRequestPayload } from 'core/types';
import { uomService } from 'parts/services';
import { Level, Size } from 'theme/enums';

import { BULK_UOM_FIELD_LABEL_KEYS, BULK_UOM_FIELD_ORDER, BulkUomField } from './enums';
import { IBulkUpdateUomsForm } from './types';

interface IProps {
  partCount: number;
  isOpen: boolean;
  onClose: (reset?: UseFormReset<IBulkUpdateUomsForm>) => void;
  onSubmit: (reset?: UseFormReset<IBulkUpdateUomsForm>) => (data: IBulkUpdateUomsForm) => void;
}

// Base UOM is picked from the master list rather than typed, the same source the
// single-part UOM form uses.
const getUomBaseOptions = (requestPayload: IRequestPayload, signal: AbortSignal) =>
  uomService.getManyUoms({ ...requestPayload, filters: { isBase: 1 } }, signal);

export const BulkUpdateUomsFormFields = ({ partCount, isOpen, onClose, onSubmit }: IProps) => {
  const { t } = useTranslation();

  const { control, errors, watch } = useFormContext();

  // Nothing ticked means nothing to send; the submit stays off until a field is
  // chosen rather than accepting a click that does nothing.
  const hasFieldSelected = BULK_UOM_FIELD_ORDER.some(
    (field) => !!watch(`edit${upperFirst(field)}`)
  );

  return (
    <Form
      title={t('titleBulkUpdateUoms')}
      subtitle={t('messageBulkUpdateUomsSubtitle', { count: partCount })}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      submitBtnLabel={t('buttonLabelUpdateUoms')}
      disabledSubmit={!hasFieldSelected}
    >
      <Stack spacing={2}>
        <Typography level={Level.BODY_SM}>{t('messageBulkUpdateUomsHint')}</Typography>

        {BULK_UOM_FIELD_ORDER.map((field) => {
          const editFieldName = `edit${upperFirst(field)}`;
          const isSelected = !!watch(editFieldName);
          const label = t(BULK_UOM_FIELD_LABEL_KEYS[field]);

          return (
            <Stack key={field} spacing={1}>
              <FormField
                name={editFieldName}
                type={InputType.CHECKBOX}
                inputProps={{
                  control,
                  error: errors?.[editFieldName] as unknown,
                  label,
                  size: Size.MD,
                }}
              />

              {isSelected && BulkUomField.BASE_UOM === field && (
                <FormField
                  name={field}
                  type={InputType.AUTOCOMPLETE}
                  inputProps={{
                    control,
                    error: errors?.[field],
                    label,
                    required: true,
                    disableClearable: true,
                    getOptions: getUomBaseOptions,
                    optionKey: 'name',
                    enabledOnFocus: false,
                  }}
                />
              )}

              {isSelected && BulkUomField.BASE_UOM !== field && (
                <FormField
                  name={field}
                  type={InputType.TEXT_INPUT}
                  inputProps={{ control, error: errors?.[field], label }}
                />
              )}
            </Stack>
          );
        })}
      </Stack>
    </Form>
  );
};
